import React, { useEffect, useState } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

function fmtDate(s) {
  if (!s) return '–';
  try { return new Date(s).toLocaleDateString('en-GB'); } catch { return s; }
}

const STATUS_BADGES = {
  pending: 'bg-warning text-dark',
  reviewed: 'bg-success',
  flagged: 'bg-danger',
};


export default function Statements() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    client.get('/statements/', { params })
      .then(r => setStatements(r.data.statements || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value)); setPage(1); };

  const statementRows = statements.filter(s => s.doc_type === 'statement');
  const remittanceRows = statements.filter(s => s.doc_type === 'remittance');
  const totalPages = Math.max(1, Math.ceil(statements.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = statements.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <div className="row g-3 mb-0">
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: '4px solid #0d6efd' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#0d6efd' }}>{statements.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Total Documents</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: '4px solid #6f42c1' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#6f42c1' }}>{statementRows.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Statements</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: '4px solid #20c997' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#20c997' }}>{remittanceRows.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Remittances</div>
            </div>
          </div>
        </div>
      </div>

      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPage(1); }}
      />

      <div className="card">
        <div className="card-header py-2">
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Statements & Remittances ({statements.length})</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Sender / Supplier</th>
                    <th>Type</th>
                    <th>Filename</th>
                    <th>Mailbox</th>
                    <th>Received</th>
                    <th>Confidence</th>
                    <th>Review Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-muted py-4">No statements or remittances found</td></tr>
                  )}
                  {paginated.map(s => (
                    <tr key={s.document_id}>
                      <td>{s.sender || '–'}</td>
                      <td>
                        <span className={`badge ${s.doc_type === 'statement' ? 'bg-primary' : 'bg-info text-dark'}`}>
                          {s.doc_type}
                        </span>
                      </td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.filename || '–'}
                      </td>
                      <td>{s.mailbox || '–'}</td>
                      <td>{fmtDate(s.received_at)}</td>
                      <td>
                        {s.classification_confidence != null
                          ? `${(s.classification_confidence * 100).toFixed(0)}%`
                          : '–'}
                      </td>
                      <td>
                        {s.review_status ? (
                          <span className={`badge ${STATUS_BADGES[s.review_status] || 'bg-secondary'}`}>
                            {s.review_status}
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted border">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && statements.length > 0 && (
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            total={statements.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
}
